import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Artist } from '../types/artist';
import { artistApi } from '../services/artistApi';
import { useAuth } from '../contexts/AuthContext';
import { ArtistProfile } from './shared/ArtistProfile';
import { ArtistForm } from './shared/ArtistForm';
import { ChangePasswordForm } from './shared/ChangePasswordForm';
import './ArtistManager.css';

type ViewMode = 'loading' | 'no-artist' | 'view-artist' | 'edit-artist' | 'create-artist';

export const ArtistManager: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>('loading');
  const [artist, setArtist] = useState<Artist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { user } = useAuth();

  const loadUserArtist = useCallback(async (initialEditMode: boolean = false) => {
    if (!user) return;

    try {
      setError(null);
      setViewMode('loading');
      
      // Get artists for this specific user
      const artists = await artistApi.getArtistsByUserId(user.id);
      
      if (artists.length > 0) {
        // Use the first artist (users typically have one artist profile)
        setArtist(artists[0]);
        // If initialEditMode is true, go straight to edit mode
        setViewMode(initialEditMode ? 'edit-artist' : 'view-artist');
      } else {
        setArtist(null);
        setViewMode('no-artist');
      }
    } catch (error: any) {
      console.error('Failed to load artist:', error);
      setError(error.message || 'Failed to load artist information');
      setViewMode('no-artist');
    }
  }, [user]);

  // Check for edit parameter on mount and load artist accordingly
  useEffect(() => {
    if (user) {
      const shouldEdit = searchParams.get('edit') === 'true';
      
      // Load artist with edit mode flag
      loadUserArtist(shouldEdit);
      
      // Clear the query parameter after reading it
      if (shouldEdit) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('edit');
        setSearchParams(newParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only run when user changes to avoid infinite loops from searchParams/setSearchParams

  // Log when artist data changes
  useEffect(() => {
    console.log('ArtistManager - artist data changed:', artist ? 'loaded' : 'null', 'viewMode:', viewMode);
  }, [artist, viewMode]);

  const handleCreateArtist = () => {
    setViewMode('create-artist');
  };

  const handleEditArtist = () => {
    setViewMode('edit-artist');
  };

  const handleSaveArtist = (savedArtist: Artist) => {
    setArtist(savedArtist);
    setViewMode('view-artist');
  };

  const handleDeleteArtist = (artistId: number) => {
    setArtist(null);
    setViewMode('no-artist');
  };

  const handleCancel = () => {
    if (artist) {
      setViewMode('view-artist');
    } else {
      setViewMode('no-artist');
    }
  };

  if (viewMode === 'loading') {
    return (
      <div className="artist-loading">
        Loading artist information...
      </div>
    );
  }

  if (viewMode === 'no-artist') {
    return (
      <div className="no-artist-container">
        <h3 className="no-artist-title">No Artist Profile</h3>
        <p className="no-artist-description">
          You don't have an artist profile yet. Create one to start sharing your music and receiving feedback.
        </p>
        {error && (
          <div className="artist-error-message" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}
        <button onClick={handleCreateArtist} className="create-artist-button">
          Create Artist Profile
        </button>
      </div>
    );
  }

  if (viewMode === 'create-artist' || viewMode === 'edit-artist') {
    return (
      <ArtistForm
        artist={viewMode === 'edit-artist' ? artist || undefined : undefined}
        onSave={handleSaveArtist}
        onCancel={handleCancel}
      />
    );
  }

  if (viewMode === 'view-artist' && artist) {
    return (
      <div className="artist-manager-content">
        <ArtistProfile
          artist={artist}
          onEdit={handleEditArtist}
          onDelete={handleDeleteArtist}
        />
        
        {/* Account Settings Section */}
        <div className="account-settings-section">
          <h3 className="account-settings-title">Account Settings</h3>
          
          {!showChangePassword ? (
            <button 
              onClick={() => setShowChangePassword(true)} 
              className="change-password-toggle-button"
            >
              Change Password
            </button>
          ) : (
            <ChangePasswordForm 
              onSuccess={() => setShowChangePassword(false)}
              onCancel={() => setShowChangePassword(false)}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};